import React, { useMemo, useState } from "react";
import SiteLayout from "../../components/layout/SiteLayout";
import PageIntro from "../../components/ui/PageIntro";
import { submitPanchangIntake } from "../../services/api/http";
import {
  CONSULTATION_SECTIONS,
  PERSONAL_FIELDS,
  buildSubmissionPayload,
  countSelections,
  createInitialFormData,
} from "./panchangConsultationData";
import "./PanchangConsultationPage.css";

function TopicButton({ groupKey, title, selected, onToggle }) {
  return (
    <button
      type="button"
      className={`panchang-topic-button ${selected ? "is-selected" : ""}`}
      onClick={() => onToggle(groupKey, title)}
      aria-pressed={selected}
    >
      {title}
    </button>
  );
}

function FormField({ field, value, onChange }) {
  return (
    <label className={`form-field panchang-field ${field.fullWidth ? "panchang-field-full" : ""}`}>
      <span>{field.label}</span>
      {field.type === "select" ? (
        <select
          name={field.name}
          value={value}
          onChange={onChange}
          className="form-control modern-input"
          required={field.required}
        >
          <option value="">Select</option>
          {field.options.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      ) : (
        <input
          type={field.type}
          name={field.name}
          value={value}
          onChange={onChange}
          className="form-control modern-input"
          placeholder={field.placeholder}
          required={field.required}
        />
      )}
    </label>
  );
}

export default function PanchangConsultationPage() {
  const [formData, setFormData] = useState(createInitialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const selectedCount = useMemo(() => countSelections(formData), [formData]);

  const handlePersonalChange = (event) => {
    const { name, value } = event.target;
    setError("");
    setSuccessMessage("");
    setFormData((current) => ({
      ...current,
      personalInformation: {
        ...current.personalInformation,
        [name]: value,
      },
    }));
  };

  const handleChecklistToggle = (groupKey, option) => {
    setError("");
    setSuccessMessage("");
    setFormData((current) => {
      const currentValues = current.checkedItems[groupKey] || [];
      const nextValues = currentValues.includes(option)
        ? currentValues.filter((value) => value !== option)
        : [option];

      return {
        ...current,
        checkedItems: {
          ...current.checkedItems,
          [groupKey]: nextValues,
        },
      };
    });
  };

  const handleTextChange = (event) => {
    const { name, value } = event.target;
    setError("");
    setSuccessMessage("");
    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSuccessMessage("");
    setIsSubmitting(true);

    try {
      await submitPanchangIntake(buildSubmissionPayload(formData));
      setSuccessMessage("Your request has been added to the queue.");
      setFormData(createInitialFormData());
    } catch (submissionError) {
      const responseMessage =
        submissionError?.response?.data?.message || "Unable to submit the intake form.";
      setError(responseMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SiteLayout>
      <main className="page-shell panchang-page-shell home-v3-shell">
        <PageIntro
          eyebrow="Spirituality / Consultation"
          title="Panchang consultation intake."
          description="Enter the basic details, choose what you need help with, and add any notes. One request can cover the rest."
          visual={(
            <div className="home-v3-panel panchang-hero-panel">
              <div>
                <span className="home-v3-kicker">Quick View</span>
                <h2>What I need</h2>
                <p>Basic birth details, one request topic, and a short note if needed.</p>
              </div>
              <div className="panchang-intake-summary-grid">
                <div className="panchang-summary-card">
                  <span className="panchang-summary-label">Fields</span>
                  <strong className="panchang-summary-value">{PERSONAL_FIELDS.length}</strong>
                </div>
                <div className="panchang-summary-card">
                  <span className="panchang-summary-label">Topics</span>
                  <strong className="panchang-summary-value">{CONSULTATION_SECTIONS.length}</strong>
                </div>
              </div>
              <p className="panchang-note">The form stays short and goes straight to the queue.</p>
            </div>
          )}
        />

        <section className="section panchang-workspace-section">
          <div className="home-v3-panel card-pad panchang-simple-workspace">
            <form className="panchang-simple-form" onSubmit={handleSubmit}>
              <div className="panchang-section-title panchang-subsection-head">
                <div>
                  <span className="home-v3-kicker">Step 1</span>
                  <h2>Your details</h2>
                  <p>Fill these in once.</p>
                </div>
                <span className="pill">{selectedCount} selected</span>
              </div>

              <div className="panchang-form-grid panchang-simple-grid">
                {PERSONAL_FIELDS.map((field) => (
                  <FormField
                    key={field.name}
                    field={field}
                    value={formData.personalInformation[field.name]}
                    onChange={handlePersonalChange}
                  />
                ))}
              </div>

              <div className="panchang-simple-section">
                <div className="panchang-section-title panchang-subsection-head">
                  <div>
                    <span className="home-v3-kicker">Step 2</span>
                    <h2>What do you need help with?</h2>
                  </div>
                </div>
                <div className="panchang-topic-grid">
                  {CONSULTATION_SECTIONS.map((section) => {
                    const selected = formData.checkedItems[section.key].includes(section.title);
                    return (
                      <TopicButton
                        key={section.key}
                        groupKey={section.key}
                        title={section.title}
                        selected={selected}
                        onToggle={handleChecklistToggle}
                      />
                    );
                  })}
                </div>
              </div>

              <div className="panchang-simple-section">
                <div className="panchang-section-title panchang-subsection-head">
                  <div>
                    <span className="home-v3-kicker">Step 3</span>
                    <h2>Notes</h2>
                  </div>
                </div>
                <label className="form-field panchang-field-full">
                  <span>Anything else I should know?</span>
                  <textarea
                    name="additionalQuestionsOrConcerns"
                    value={formData.additionalQuestionsOrConcerns}
                    onChange={handleTextChange}
                    className="form-control modern-input"
                    rows={4}
                    placeholder="Add dates, timing, symptoms, or any other detail."
                  />
                </label>
              </div>

              <div className="panchang-submit-row">
                <button type="submit" className="home-v3-button is-primary panchang-submit-button" disabled={isSubmitting}>
                  {isSubmitting ? "Submitting..." : "Add to Queue"}
                </button>
                <p className="panchang-note">Your request will go straight to the queue.</p>
              </div>

              {error ? <p className="birth-chart-error panchang-field-full">{error}</p> : null}
              {successMessage ? <p className="panchang-note panchang-field-full">{successMessage}</p> : null}
            </form>
          </div>
        </section>
      </main>
    </SiteLayout>
  );
}
